<template>
  <div class="company-view-wrapper">
    <!-- Toast Notifications -->
    <div class="toast-container">
      <CToaster :autohide="3000">
        <CToast
          v-for="(info, index) in infoList"
          :key="`toast-${index}-${Date.now()}`"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}
        </CToast>
      </CToaster>
    </div>

    <!-- Page Header -->
    <CRow class="mb-4">
      <CCol>
        <div class="page-header">
          <h4 class="mb-0">
            <i class="fas fa-building me-2 text-primary"></i>
            <strong>Company Settings</strong>
          </h4>
          <small class="text-muted">View general company information and bank details</small>
        </div>
      </CCol>
    </CRow>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="text-center py-5">
        <CSpinner color="primary" />
        <p class="text-muted mt-3">Loading company settings...</p>
      </div>
    </div>

    <!-- Company View -->
    <CRow v-else>
      <CCol sm="12">
        <CCard class="view-card">
          <CCardHeader class="view-header">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <i class="fas fa-info-circle me-2 text-primary"></i>
                <strong>General Company Information</strong>
              </div>
              <CBadge color="info">Read Only</CBadge>
            </div>
          </CCardHeader>
          <CCardBody>
            <CForm>
              <!-- Company Information Section -->
              <!-- Name -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Name</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.name"
                    readonly
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-building me-1"></i>
                    Official registered company name
                  </small>
                </CCol>
              </CRow>

              <!-- Registration No -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Reg No</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.regNo"
                    readonly
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-id-card me-1"></i>
                    Company registration number
                  </small>
                </CCol>
              </CRow>

              <!-- Tagline -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Tagline</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.tagLine"
                    readonly
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-comment me-1"></i>
                    Company slogan or tagline
                  </small>
                </CCol>
              </CRow>

              <!-- Address -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Address</strong>
                </CCol>
                <CCol sm="9">
                  <CTextarea
                    v-model="obj.address"
                    readonly
                    rows="5"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-map-marker-alt me-1"></i>
                    Full registered business address
                  </small>
                </CCol>
              </CRow>

              <!-- Phone -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Phone</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.phone"
                    readonly
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-phone me-1"></i>
                    Primary contact phone number
                  </small>
                </CCol>
              </CRow>

              <!-- Company Logo -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Company Logo</strong>
                </CCol>
                <CCol sm="9">
                  <CImg
                    v-if="obj.logoUrl"
                    :src="getImageUrl()"
                    class="mb-2"
                    thumbnail
                    style="max-width: 300px;"
                  />
                  <div v-else class="no-logo">
                    <i class="fas fa-image fa-3x text-muted mb-2"></i>
                    <p class="text-muted">No logo uploaded</p>
                  </div>
                  <small class="form-text text-muted">
                    <i class="fas fa-info-circle me-1"></i>
                    Company logo image
                  </small>
                </CCol>
              </CRow>

              <!-- Divider -->
              <hr class="my-4" />

              <!-- Bank Information Section Header -->
              <CRow class="mb-3">
                <CCol>
                  <h6 class="section-header">
                    <i class="fas fa-university me-2 text-primary"></i>
                    <strong>Bank Information</strong>
                  </h6>
                </CCol>
              </CRow>

              <!-- Bank Name -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Bank Name</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.bankName"
                    readonly
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-university me-1"></i>
                    Name of the bank
                  </small>
                </CCol>
              </CRow>

              <!-- Bank Address -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Bank Address</strong>
                </CCol>
                <CCol sm="9">
                  <CTextarea
                    v-model="obj.bankAddress"
                    readonly
                    rows="5"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-map-marker-alt me-1"></i>
                    Full bank branch address
                  </small>
                </CCol>
              </CRow>

              <!-- Bank Account Name -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Bank Account Name</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.bankAccountName"
                    readonly
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-user me-1"></i>
                    Name on the bank account
                  </small>
                </CCol>
              </CRow>

              <!-- Bank Account No -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Bank Account No</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.bankAccountNo"
                    readonly
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-credit-card me-1"></i>
                    Bank account number
                  </small>
                </CCol>
              </CRow>
            </CForm>
          </CCardBody>

          <!-- View Footer -->
          <CCardFooter class="view-footer">
            <div class="d-flex justify-content-between align-items-center">
              <CButton
                color="primary"
                @click="onEdit"
              >
                <i class="fas fa-edit me-2"></i>
                Edit Settings
              </CButton>
              <small class="text-muted">
                <i class="fas fa-info-circle me-1"></i>
                Click Edit to modify company settings
              </small>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import CompanyApi from "../../lib/companyApi";

export default {
  name: "Company",
  data() {
    return {
      infoList: [],
      api: new CompanyApi(),
      obj: {
        logoUrl: "",
      },
      loading: false,
    };
  },
  mounted() {
    this.resetObj();
  },
  methods: {
    removeTrailingSlash(str) {
      if (str.endsWith("/")) {
        return str.slice(0, -1);
      }
      return str;
    },

    getImageUrl() {
      var url = this.removeTrailingSlash(apiUrl) + this.obj.logoUrl;
      return url;
    },

    onEdit() {
      this.$router.push({
        path: `/admins/companyEdit`,
      });
    },

    resetObj() {
      this.loading = true;
      this.api
        .getList()
        .then((response) => {
          this.obj = response.result[0] || {};
          this.loading = false;
        })
        .catch(({ data }) => {
          this.toast("Error", helper.getErrorMessage(data), "danger");
          this.loading = false;
        });
    },

    toast(header, message, color) {
      this.infoList.push({
        header: header,
        message: message,
        color: color,
      });
    },
  },
};
</script>

<style scoped>
/* Professional Container */
.company-view-wrapper {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Page Header */
.page-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
}

.page-header h4 {
  font-size: 1.5rem;
  color: var(--cui-body-color, #212529);
}

.page-header small {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
}

/* Loading State */
.loading-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* View Card */
.view-card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.view-header {
  background: white;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
}

.view-header strong {
  font-size: 1.1rem;
  color: var(--cui-body-color, #212529);
}

/* View Body */
.view-card .card-body {
  padding: 2rem 1.5rem;
}

/* Form Labels */
.col-form-label {
  font-weight: 500;
  color: var(--cui-body-color, #495057);
}

/* Form Help Text */
.form-text {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

/* Section Header */
.section-header {
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--cui-border-color, #e9ecef);
}

/* Divider */
hr {
  border-top: 1px solid var(--cui-border-color, #dee2e6);
}

/* No Logo State */
.no-logo {
  min-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--cui-body-bg, #f8f9fa);
  border: 2px dashed var(--cui-border-color, #dee2e6);
  border-radius: 0.5rem;
  padding: 2rem;
}

/* View Footer */
.view-footer {
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .company-view-wrapper {
    padding: 1rem;
  }

  .page-header h4 {
    font-size: 1.25rem;
  }

  .view-card .card-body {
    padding: 1.5rem 1rem;
  }

  .col-form-label {
    margin-bottom: 0.5rem;
  }
}

/* Print Styles */
@media print {
  .toast-container,
  .view-footer {
    display: none !important;
  }

  .view-card {
    box-shadow: none;
    border: 1px solid #dee2e6;
  }
}
</style>
